import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowDown, ArrowUp, ChartColumn, ChevronDown, ChevronLeft, CircleCheck, ClipboardCheck, Clock,
  Copy, GripVertical, Layers, ListChecks, ListPlus, Pencil, Plus, SlidersHorizontal, Trash2,
  TriangleAlert, X,
} from 'lucide-react';
import { getQuizzes, createQuiz, updateQuiz, deleteQuiz } from '../../api/quizzes';
import { getLessons } from '../../api/lessons';
import { getCourseModules } from '../../api/modules';
import { unwrapList } from '../../utils/api';
import errorText from '../../utils/errorText';
import LoadingState from '../../components/LoadingState/LoadingState';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import MetricCard from '../../components/MetricCard/MetricCard';
import SearchInput from '../../components/SearchInput/SearchInput';
import { useConfirmDialog } from '../../components/ConfirmDialog/ConfirmDialog';
import ActionMenu from '../../components/ActionMenu/ActionMenu';
import toast from 'react-hot-toast';
import Dropdown from '../../components/Dropdown/Dropdown';
import { useScrollFade } from '../../hooks/useScrollFade';
import { useHideBottomNav } from '../../components/Layout/shellState';
import { useI18n } from '../../i18n/I18nContext';
import { pluralKey } from '../../utils/plural';
import {
  OPTION_KEYS, normalizeQuestion, toApiQuestion, withoutOption, hasCorrectAnswer, newQuestion, copyQuestion,
  fromParsedQuestion, readTimeLimit, readPassMark, MAX_TIME_LIMIT_MINUTES,
} from './quizQuestions';
import AnswerOptions from './AnswerOptions';
import BulkQuestionsDialog from './BulkQuestionsDialog';
import MediaSlot from '../../components/MediaSlot/MediaSlot';
import './CourseQuizzes.css';

// Pass mark the backend grades a quiz with when none was authored (QuizzesService.DEFAULT_PASSING_SCORE).
const DEFAULT_PASS_MARK = 60;

// One tap for the usual lengths; any other whole number of minutes can be typed
const TIME_PRESETS = [10, 15, 20, 30];

// Where "add a lesson first" leads: the course's lessons, with the add form open (CourseLessons reads add=1)
export const addLessonPath = (courseId) => `/courses/edit/${courseId}?tab=curriculum&c=lessons&add=1`;

/**
 * Lesson numbers as teachers count them: 1, 2, 3… in course order. The stored orderIndex starts at 0 in
 * courses the CRM renumbered and at 1 in seeded ones, so it is not shown as is.
 */
function lessonNumbersOf(lessons) {
  const map = {};
  lessons.forEach((l, i) => { map[l.id] = i + 1; });
  return map;
}

/* ── Full-page Quiz Editor ───────────────────────────────────────────────── */
function QuizEditor({ editingId, formData, setFormData, lessons, lessonNumbers, modules, quizByLesson, _courseId, saving, formError, embedded, onSave, onClose }) {
  const { t, lang } = useI18n();
  const [dragIdx, setDragIdx] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const bottomRef = useRef(null);
  const rootRef = useRef(null);
  const sidebarSettingsToggleRef = useRef(null);
  const sidebarCloseRef = useRef(null);
  const sidebarRef = useRef(null);
  const sidebarWasOpenRef = useRef(false);

  useEffect(() => {
    if (!mobileSidebarOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') { setMobileSidebarOpen(false); return; }
      if (e.key !== 'Tab') return;
      const sidebar = sidebarRef.current;
      if (!sidebar) return;
      const focusable = Array.from(
        sidebar.querySelectorAll(
          'button:not(:disabled),[href],input:not(:disabled),select:not(:disabled),textarea:not(:disabled),[tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el.offsetParent !== null);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mobileSidebarOpen]);

  // Embedded, the editor sits under the course hero and tabs: bring it to the top of the workspace so
  // its viewport-sized box (see .cq-editor--embedded) fits the screen.
  useEffect(() => {
    if (embedded) rootRef.current?.scrollIntoView?.({ block: 'start' });
  }, [embedded]);

  useEffect(() => {
    if (mobileSidebarOpen) {
      sidebarWasOpenRef.current = true;
      sidebarCloseRef.current?.focus();
    } else if (sidebarWasOpenRef.current) {
      sidebarWasOpenRef.current = false;
      sidebarSettingsToggleRef.current?.focus();
    }
  }, [mobileSidebarOpen]);
  const lessonForForm = lessons.find(l => l.id === formData.lessonId);
  const moduleForLesson = lessonForForm && modules.find(m => m.id === lessonForForm.moduleId);

  const updateQuestion = (idx, updates) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => i === idx ? { ...q, ...updates } : q),
    }));
  };

  const removeQuestion = (idx) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== idx),
    }));
  };

  const duplicateQuestion = (idx) => {
    setFormData(prev => {
      const questions = [...prev.questions];
      questions.splice(idx + 1, 0, copyQuestion(prev.questions[idx]));
      return { ...prev, questions };
    });
  };

  const addNewQuestion = () => {
    setFormData(prev => ({ ...prev, questions: [...prev.questions, newQuestion()] }));
    setTimeout(() => bottomRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' }), 80);
  };

  // The list from "Ro'yxatdan qo'shish" goes after the questions already there; the first new card is brought into view
  const addParsedQuestions = (parsed) => {
    const firstNew = formData.questions.length;
    setFormData(prev => ({ ...prev, questions: [...prev.questions, ...parsed.map(fromParsedQuestion)] }));
    setBulkOpen(false);
    toast.success(t(pluralKey('quizAuthoring.bulk.added', parsed.length, lang), { count: parsed.length }));
    setTimeout(() => document.getElementById(`qcard-${firstNew}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }), 80);
  };

  const timeLimit = readTimeLimit(formData.timeLimitMinutes);
  const passMark = readPassMark(formData.passingScore);

  const moveQuestion = (from, to) => {
    if (from === to || to < 0) return;
    setFormData(prev => {
      if (to >= prev.questions.length) return prev;
      const questions = [...prev.questions];
      const [item] = questions.splice(from, 1);
      questions.splice(to, 0, item);
      return { ...prev, questions };
    });
  };

  // Only the grip is draggable: a draggable card would swallow the text selection inside its fields.
  const handleDragStart = (e, idx) => {
    setDragIdx(idx);
    try {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(idx));
      const card = document.getElementById(`qcard-${idx}`);
      if (card && e.dataTransfer.setDragImage) e.dataTransfer.setDragImage(card, 24, 24);
    } catch {
      /* drag image is cosmetic */
    }
  };
  const handleDragOver = (e, idx) => { if (dragIdx === null) return; e.preventDefault(); setDragOverIdx(idx); };
  const handleDrop = (idx) => { if (dragIdx !== null) moveQuestion(dragIdx, idx); setDragIdx(null); setDragOverIdx(null); };
  const handleDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };

  const updateOption = (qIdx, optIdx, value) => {
    updateQuestion(qIdx, { options: formData.questions[qIdx].options.map((o, i) => i === optIdx ? value : o) });
  };

  const addOption = (qIdx) => {
    const q = formData.questions[qIdx];
    if (q.options.length >= 6) return;
    updateQuestion(qIdx, { options: [...q.options, ''] });
  };

  const removeOption = (qIdx, optIdx) => {
    const q = formData.questions[qIdx];
    if (q.options.length <= 2) return;
    updateQuestion(qIdx, withoutOption(q, optIdx));
  };

  return (
    <div ref={rootRef} className={`cq-editor section-card ${embedded ? 'cq-editor--embedded' : ''}`}>
      <div className="cq-editor-header">
        <div className="cq-editor-header-left">
          <button
            type="button"
            className="ds-btn ds-btn-secondary ds-btn-icon cq-editor-back"
            onClick={onClose}
            aria-label={t('courseAssess.quizzes.backToList')}
            title={t('courseAssess.quizzes.backToList')}
          >
            <ChevronLeft aria-hidden="true" />
          </button>
          <div className="cq-editor-title-group">
            <div className="cq-editor-title-row">
              <h2 className="cq-editor-title">{editingId ? t('courseAssess.quizzes.editQuiz') : t('courseAssess.quizzes.newQuiz')}</h2>
              {lessonForForm && (
                <span className="ds-badge ds-badge-brand">{t('courseAssess.quizzes.lessonNumberBadge', { index: lessonNumbers[lessonForForm.id] ?? '—' })}</span>
              )}
            </div>
            {lessonForForm && (
              <div className="cq-editor-lesson-row">
                <span className="cq-editor-lesson-name">{lessonForForm.title}</span>
                {moduleForLesson && (
                  <span className="ds-badge ds-badge-neutral cq-editor-module-tag" title={moduleForLesson.title}>
                    <span>{moduleForLesson.title}</span>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="cq-editor-header-right">
          <button
            type="button"
            ref={sidebarSettingsToggleRef}
            className="ds-btn ds-btn-secondary ds-btn-icon cq-editor-settings-toggle"
            onClick={() => setMobileSidebarOpen(true)}
            title={t('courseAssess.quizzes.openSettingsTooltip')}
            aria-label={t('courseAssess.quizzes.openSettingsTooltip')}
            aria-expanded={mobileSidebarOpen}
          >
            <SlidersHorizontal aria-hidden="true" />
          </button>
          <div className="cq-editor-meta">
            <span className="ds-badge ds-badge-neutral cq-editor-qcount">{t(pluralKey('courseAssess.quizzes.questionCount', formData.questions.length, lang), { count: formData.questions.length })}</span>
            {timeLimit.minutes && (
              <span className="ds-badge ds-badge-brand cq-editor-timer-badge">
                <Clock size={13} aria-hidden="true" />
                {t('courseAssess.quizzes.minutesBadge', { count: timeLimit.minutes })}
              </span>
            )}
          </div>
          <button
            type="button"
            className={`ds-btn ds-btn-primary cq-editor-save ${saving ? 'is-loading' : ''}`}
            onClick={onSave}
            disabled={saving}
          >
            {editingId ? t('courseAssess.quizzes.saveChanges') : t('courseAssess.quizzes.createQuizBtn')}
          </button>
        </div>
      </div>

      {formError && (
        <div className="ui-alert cq-form-error" role="alert">
          <TriangleAlert aria-hidden="true" />
          <p>{formError}</p>
        </div>
      )}

      <div className="cq-editor-body">
        {mobileSidebarOpen && (
          <div className="cq-editor-sidebar-backdrop" onClick={() => setMobileSidebarOpen(false)} />
        )}
        <div
          ref={sidebarRef}
          className={`cq-editor-sidebar ${mobileSidebarOpen ? 'cq-editor-sidebar--open' : ''}`}
          role={mobileSidebarOpen ? 'dialog' : undefined}
          aria-modal={mobileSidebarOpen ? 'true' : undefined}
          aria-label={mobileSidebarOpen ? t('courseAssess.quizzes.settingsDrawerTitle') : undefined}
        >
          <div className="cq-editor-sidebar-mhead">
            <span className="cq-editor-sidebar-mtitle">{t('courseAssess.quizzes.settingsDrawerTitle')}</span>
            <button
              type="button"
              ref={sidebarCloseRef}
              className="ds-btn ds-btn-secondary ds-btn-icon ds-btn-sm cq-editor-sidebar-close"
              onClick={() => setMobileSidebarOpen(false)}
              title={t('courseAssess.quizzes.closeSettingsTooltip')}
              aria-label={t('courseAssess.quizzes.closeSettingsTooltip')}
            >
              <X aria-hidden="true" />
            </button>
          </div>
          {!editingId && (
            <div className="cq-editor-field">
              <label className="cq-label" htmlFor="cq-lesson">{t('courseAssess.quizzes.lessonLabel')} <span className="cq-required">*</span></label>
              <Dropdown
                id="cq-lesson"
                value={formData.lessonId}
                onChange={(val) => setFormData(prev => ({ ...prev, lessonId: val }))}
                options={lessons.filter(l => !quizByLesson[l.id] || l.id === formData.lessonId).map(l => ({
                  value: l.id,
                  label: `#${lessonNumbers[l.id]} ${l.title}`,
                }))}
                placeholder={t('courseAssess.quizzes.selectLessonPlaceholder')}
                ariaLabel={t('courseAssess.quizzes.lessonLabel')}
              />
            </div>
          )}
          <div className="cq-editor-field">
            <label className="cq-label" htmlFor="cq-pass-score">{t('courseAssess.quizzes.passingScoreLabel')}</label>
            <div className="cq-score-input">
              <input
                id="cq-pass-score"
                type="number"
                className="cq-input"
                min="0"
                max="100"
                step="1"
                inputMode="numeric"
                value={formData.passingScore}
                aria-invalid={!passMark.valid || undefined}
                aria-describedby={passMark.valid ? undefined : 'cq-pass-score-error'}
                onChange={e => setFormData(prev => ({ ...prev, passingScore: e.target.value }))}
              />
              <span className="cq-score-pct" aria-hidden="true">%</span>
            </div>
            {!passMark.valid && <p id="cq-pass-score-error" className="cq-field-error">{t('quizAuthoring.common.passMarkInvalid')}</p>}
          </div>
          <div className="cq-editor-field">
            <label className="cq-label" htmlFor="cq-time-limit">{t('courseAssess.quizzes.timeLimitLabel')}</label>
            <div className="cq-score-input cq-time-input">
              <input
                id="cq-time-limit"
                type="number"
                className="cq-input"
                min="1"
                max={MAX_TIME_LIMIT_MINUTES}
                step="1"
                inputMode="numeric"
                placeholder={t('quizAuthoring.quiz.timeLimitPlaceholder')}
                value={formData.timeLimitMinutes}
                aria-invalid={!timeLimit.valid || undefined}
                aria-describedby={timeLimit.valid ? 'cq-time-limit-hint' : 'cq-time-limit-error'}
                onChange={e => setFormData(prev => ({ ...prev, timeLimitMinutes: e.target.value }))}
              />
              <span className="cq-score-pct" aria-hidden="true">{t('quizAuthoring.quiz.timeLimitUnit')}</span>
            </div>
            <div className="cq-time-presets" role="group" aria-label={t('courseAssess.quizzes.timeLimitLabel')}>
              <button
                type="button"
                className={`cq-time-chip ${String(formData.timeLimitMinutes).trim() === '' ? 'is-active' : ''}`}
                aria-pressed={String(formData.timeLimitMinutes).trim() === ''}
                onClick={() => setFormData(prev => ({ ...prev, timeLimitMinutes: '' }))}
              >
                {t('quizAuthoring.quiz.noLimitAction')}
              </button>
              {TIME_PRESETS.map((m) => (
                <button
                  key={m}
                  type="button"
                  className={`cq-time-chip ${timeLimit.minutes === m ? 'is-active' : ''}`}
                  aria-pressed={timeLimit.minutes === m}
                  onClick={() => setFormData(prev => ({ ...prev, timeLimitMinutes: String(m) }))}
                >
                  {t('courseAssess.quizzes.minutesOption', { count: m })}
                </button>
              ))}
            </div>
            {timeLimit.valid
              ? <p id="cq-time-limit-hint" className="cq-field-hint">{t('quizAuthoring.quiz.timeLimitHint')}</p>
              : <p id="cq-time-limit-error" className="cq-field-error">{t('quizAuthoring.quiz.timeLimitInvalid')}</p>}
            {timeLimit.minutes && formData.questions.length > 0 && (
              <div className="ui-alert cq-time-calc" data-tone="success">
                <Clock aria-hidden="true" />
                <p>{t('courseAssess.quizzes.perQuestionEstimate', { seconds: Math.round((timeLimit.minutes * 60) / formData.questions.length) })}</p>
              </div>
            )}
          </div>
          <div className="cq-editor-field">
            <span className="cq-label">{t('courseAssess.quizzes.quickNavLabel')}</span>
            <div className="cq-quicknav">
              {formData.questions.map((q, idx) => (
                <button
                  key={q.id}
                  type="button"
                  className={`cq-quicknav-item ${q.text.trim() ? 'cq-quicknav-item--filled' : ''}`}
                  onClick={() => {
                    setMobileSidebarOpen(false);
                    document.getElementById(`qcard-${idx}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
                  }}
                  title={q.text || t('courseAssess.quizzes.questionTitle', { index: idx + 1 })}
                  aria-label={t('courseAssess.quizzes.questionTitle', { index: idx + 1 })}
                >
                  {idx + 1}
                </button>
              ))}
              <button type="button" className="cq-quicknav-add" onClick={addNewQuestion} title={t('courseAssess.quizzes.addQuestionTooltip')} aria-label={t('courseAssess.quizzes.addQuestionTooltip')}>
                <Plus size={16} aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>

        <div className="cq-editor-cards">
          {formData.questions.length === 0 ? (
            <PageEmptyState
              variant="docs"
              size="sm"
              title={t('courseAssess.quizzes.noQuestionsTitle')}
              description={t('courseAssess.quizzes.noQuestionsDesc')}
              actionLabel={t('quizAuthoring.bulk.open')}
              onAction={() => setBulkOpen(true)}
              secondaryLabel={t('courseAssess.quizzes.addQuestion')}
              onSecondaryAction={addNewQuestion}
            />
          ) : (
            formData.questions.map((q, idx) => (
              <div
                key={q.id}
                id={`qcard-${idx}`}
                className={`cq-qcard ${dragIdx === idx ? 'cq-qcard--dragging' : ''} ${dragOverIdx === idx ? 'cq-qcard--dragover' : ''}`}
                onDragOver={(e) => handleDragOver(e, idx)}
                onDrop={() => handleDrop(idx)}
              >
                <div className="cq-qcard-header">
                  <div
                    className="cq-qcard-drag"
                    title={t('courseAssess.quizzes.dragToReorder')}
                    draggable
                    onDragStart={(e) => handleDragStart(e, idx)}
                    onDragEnd={handleDragEnd}
                  >
                    <GripVertical size={16} aria-hidden="true" />
                  </div>
                  <span className="cq-qcard-num">{t('courseAssess.quizzes.questionTitle', { index: idx + 1 })}</span>
                  <div className="cq-qcard-actions">
                    <button type="button" className="cq-qcard-action cq-qcard-action--move" onClick={() => moveQuestion(idx, idx - 1)} disabled={idx === 0} aria-label={`${t('courseAssess.quizzes.questionTitle', { index: idx + 1 })}: ↑`}>
                      <ArrowUp size={15} aria-hidden="true" />
                    </button>
                    <button type="button" className="cq-qcard-action cq-qcard-action--move" onClick={() => moveQuestion(idx, idx + 1)} disabled={idx === formData.questions.length - 1} aria-label={`${t('courseAssess.quizzes.questionTitle', { index: idx + 1 })}: ↓`}>
                      <ArrowDown size={15} aria-hidden="true" />
                    </button>
                    <button type="button" className="cq-qcard-action" onClick={() => duplicateQuestion(idx)} title={t('courseAssess.quizzes.duplicateTooltip')} aria-label={t('courseAssess.quizzes.duplicateTooltip')}>
                      <Copy size={15} aria-hidden="true" />
                    </button>
                    <button type="button" className="cq-qcard-action cq-qcard-action--del" onClick={() => removeQuestion(idx)} title={t('courseAssess.quizzes.deleteTooltip')} aria-label={t('courseAssess.quizzes.deleteTooltip')}>
                      <Trash2 size={15} aria-hidden="true" />
                    </button>
                  </div>
                </div>

                <div className="cq-qcard-body">
                  <textarea
                    className="cq-qcard-text"
                    placeholder={t('courseAssess.quizzes.questionPlaceholder')}
                    aria-label={t('courseAssess.quizzes.questionTitle', { index: idx + 1 })}
                    rows={2}
                    value={q.text}
                    onChange={e => updateQuestion(idx, { text: e.target.value })}
                  />

                  <MediaSlot
                    kind="image"
                    className="cq-qcard-media"
                    value={q.imageUrl || ''}
                    onChange={(url) => updateQuestion(idx, { imageUrl: url, imageTouched: true })}
                  />

                  <AnswerOptions
                    options={q.options}
                    correct={[OPTION_KEYS.indexOf(q.correctAnswer)].filter(i => i >= 0)}
                    letters={OPTION_KEYS}
                    label={t('courseAssess.quizzes.answerOptionsLabel')}
                    labelClassName="cq-label"
                    onToggleCorrect={(oi) => updateQuestion(idx, { correctAnswer: OPTION_KEYS[oi] })}
                    onChangeOption={(oi, value) => updateOption(idx, oi, value)}
                    onAddOption={() => addOption(idx)}
                    onRemoveOption={(oi) => removeOption(idx, oi)}
                  />

                  <div className="cq-qcard-explanation">
                    <label className="cq-label" htmlFor={`cq-expl-${idx}`}>{t('courseAssess.quizzes.explanationLabel')} <span className="cq-hint">{t('courseAssess.quizzes.explanationHint')}</span></label>
                    <textarea
                      id={`cq-expl-${idx}`}
                      className="cq-qcard-expl-input"
                      placeholder={t('courseAssess.quizzes.explanationPlaceholder')}
                      rows={2}
                      value={q.explanation}
                      onChange={e => updateQuestion(idx, { explanation: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            ))
          )}

          {formData.questions.length > 0 && (
            <div className="cq-editor-add-row">
              <button type="button" className="ds-btn ds-btn-secondary cq-editor-add-card" onClick={addNewQuestion}>
                <Plus aria-hidden="true" />
                {t('courseAssess.quizzes.addQuestion')}
              </button>
              <button type="button" className="ds-btn ds-btn-secondary cq-editor-add-card" onClick={() => setBulkOpen(true)}>
                <ListPlus aria-hidden="true" />
                {t('quizAuthoring.bulk.open')}
              </button>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      </div>
      <BulkQuestionsDialog
        open={bulkOpen}
        existingCount={formData.questions.length}
        onClose={() => setBulkOpen(false)}
        onAdd={addParsedQuestions}
      />
    </div>
  );
}

/* ── One module's lessons: a horizontally scrolling table with an edge fade ─────────────────────── */
function LessonTable({ label, rows, lessonNumbers, courseId, onCreate, onEdit, onDelete }) {
  const { t } = useI18n();
  const { ref, fade } = useScrollFade();
  return (
    <div ref={ref} className={`ui-table-scroll ${fade}`.trim()} role="region" aria-label={label} tabIndex={0}>
      <table className="ui-table ui-table--stack cq-table">
        <thead>
          <tr>
            <th>{t('courseAssess.quizzes.colLesson')}</th>
            <th className="ui-num">{t('courseAssess.quizzes.colQuestions')}</th>
            <th className="ui-num">{t('courseAssess.quizzes.colTimer')}</th>
            <th className="ui-num">{t('courseAssess.quizzes.colPass')}</th>
            <th><span className="visually-hidden">{t('courseAssess.quizzes.colActions')}</span></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ lesson, quiz }) => (
            <tr key={lesson.id} className={quiz ? undefined : 'cq-row-missing'}>
              <td className="cq-td-lesson">
                <div className="cq-lesson-cell">
                  <span className="cq-lesson-index" aria-hidden="true">{lessonNumbers[lesson.id] ?? '—'}</span>
                  <span className="cq-lesson-text">
                    <span className="cq-lesson-title">{lesson.title}</span>
                    {!quiz && <span className="ds-badge ds-badge-warning cq-missing-badge">{t('courseAssess.quizzes.withoutTest')}</span>}
                  </span>
                </div>
              </td>
              <td className="ui-num" data-label={t('courseAssess.quizzes.colQuestions')}>
                {quiz ? <span>{quiz.questions?.length ?? 0}</span> : <span className="cq-none">—</span>}
              </td>
              <td className="ui-num" data-label={t('courseAssess.quizzes.colTimer')}>
                {quiz?.timeLimitMinutes
                  ? <span>{t('courseAssess.quizzes.minutesBadge', { count: quiz.timeLimitMinutes })}</span>
                  : <span className="cq-none">—</span>}
              </td>
              <td className="ui-num" data-label={t('courseAssess.quizzes.colPass')}>
                {quiz
                  ? <span className="cq-pass-score" title={quiz.passingScore == null ? t('courseAssess.quizzes.defaultPassMarkTooltip') : undefined}>{quiz.effectivePassingScore ?? quiz.passingScore ?? DEFAULT_PASS_MARK}%</span>
                  : <span className="cq-none">—</span>}
              </td>
              <td className="ui-row-actions-cell">
                {quiz ? (
                  <div className="ui-row-actions">
                    <Link
                      to={`/courses/${courseId}/quizzes/${quiz.id}/results`}
                      className="ui-icon-btn"
                      aria-label={t('courseAssess.quizzes.resultsLink')}
                      title={t('courseAssess.quizzes.resultsLink')}
                    >
                      <ChartColumn aria-hidden="true" />
                    </Link>
                    <button
                      type="button"
                      className="ui-icon-btn"
                      onClick={() => onEdit(quiz)}
                      aria-label={t('courseAssess.quizzes.editAction')}
                      title={t('courseAssess.quizzes.editAction')}
                    >
                      <Pencil aria-hidden="true" />
                    </button>
                    <ActionMenu
                      ariaLabel={t('courseAssess.quizzes.moreActions')}
                      items={[
                        {
                          key: 'delete',
                          label: t('courseAssess.quizzes.deleteAction'),
                          tone: 'danger',
                          onSelect: () => onDelete(quiz.id),
                        },
                      ]}
                    />
                  </div>
                ) : (
                  <div className="ui-row-actions">
                    <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={() => onCreate(lesson.id)}>
                      <Plus aria-hidden="true" />
                      {t('courseAssess.quizzes.createQuizForLesson')}
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── Main CourseQuizzes page ─────────────────────────────────────────────── */
export default function CourseQuizzes({ courseId: courseIdProp } = {}) {
  const { t, lang } = useI18n();
  const [confirm, confirmDialog] = useConfirmDialog();
  const navigate = useNavigate();
  const params = useParams();
  const courseId = courseIdProp || params.courseId || params.id;
  // The course workspace passes the course id; the standalone /courses/:courseId/quizzes route does not
  const embedded = Boolean(courseIdProp);

  const [quizzes, setQuizzes] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  // null | 'forbidden' | 'failed'
  const [loadError, setLoadError] = useState(null);

  const [search, setSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [sort, setSort] = useState('order');
  const [collapsed, setCollapsed] = useState({});

  const [showForm, setShowForm] = useState(false);
  // The editor fills the screen: on a phone the floating tab bar would cover its last question card
  useHideBottomNav(showForm);
  const [editingId, setEditingId] = useState(null);
  // timeLimitMinutes is the text of its field: '' is no limit (see readTimeLimit)
  const [formData, setFormData] = useState({ lessonId: '', passingScore: 70, questions: [], timeLimitMinutes: '' });
  // What the editor opened with: leaving it with something else asks first
  const openedWith = useRef('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  // `silent` refreshes the data without swapping the page for the loading skeleton (an open editor stays)
  const fetchAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [qRes, lRes, mRes] = await Promise.all([
        getQuizzes(courseId),
        getLessons(courseId).catch(() => []),
        getCourseModules(courseId).catch(() => []),
      ]);
      setQuizzes(unwrapList(qRes));
      setLessons(unwrapList(lRes).sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0)));
      setModules(unwrapList(mRes).sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0)));
      setLoadError(null);
    } catch (err) {
      setLoadError(err.code === 403 ? 'forbidden' : 'failed');
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const quizByLesson = useMemo(() => {
    const map = {};
    quizzes.forEach(q => { if (q.lessonId) map[q.lessonId] = q; });
    return map;
  }, [quizzes]);

  const groupedData = useMemo(() => {
    const groups = modules.map(m => ({
      module: m,
      rows: lessons.filter(l => l.moduleId === m.id).map(l => ({ lesson: l, quiz: quizByLesson[l.id] || null })),
    }));
    const assignedIds = new Set(lessons.filter(l => l.moduleId).map(l => l.id));
    const unassigned = lessons.filter(l => !assignedIds.has(l.id));
    if (unassigned.length > 0) {
      groups.push({ module: { id: '__none__', title: t('courseAssess.quizzes.noModuleGroup'), orderIndex: 9999 }, rows: unassigned.map(l => ({ lesson: l, quiz: quizByLesson[l.id] || null })) });
    }
    return groups;
  }, [modules, lessons, quizByLesson, t]);

  const filteredGroups = useMemo(() => {
    let groups = groupedData;
    if (moduleFilter) groups = groups.filter(g => g.module.id === moduleFilter);
    if (search) {
      const q = search.toLowerCase();
      groups = groups.map(g => ({ ...g, rows: g.rows.filter(({ lesson, quiz }) => lesson.title.toLowerCase().includes(q) || (quiz?.title || '').toLowerCase().includes(q)) })).filter(g => g.rows.length > 0);
    }
    if (sort === 'missing') {
      groups = groups.map(g => ({ ...g, rows: [...g.rows].sort((a, b) => { if (!a.quiz && b.quiz) return -1; if (a.quiz && !b.quiz) return 1; return (a.lesson.orderIndex || 0) - (b.lesson.orderIndex || 0); }) }));
    } else if (sort === 'alpha') {
      groups = groups.map(g => ({ ...g, rows: [...g.rows].sort((a, b) => a.lesson.title.localeCompare(b.lesson.title)) }));
    }
    return groups.filter(g => g.rows.length > 0);
  }, [groupedData, moduleFilter, search, sort]);

  const totalMissing = useMemo(() => lessons.filter(l => !quizByLesson[l.id]).length, [lessons, quizByLesson]);
  const lessonNumbers = useMemo(() => lessonNumbersOf(lessons), [lessons]);

  const openCreate = (lessonId = '') => {
    const next = { lessonId: lessonId || '', passingScore: 70, version: 0, questions: [], timeLimitMinutes: '' };
    setEditingId(null);
    setFormData(next);
    openedWith.current = JSON.stringify(next);
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = (quiz) => {
    // The mark students are graded with: the authored one, else the backend default
    const passMark = quiz.effectivePassingScore ?? quiz.passingScore ?? DEFAULT_PASS_MARK;
    const next = {
      lessonId: quiz.lessonId || '',
      passingScore: passMark,
      // The pass mark is only sent when it differs from this, so fixing a typo never pins a quiz that
      // grades at the default to a value the teacher did not choose
      initialPassingScore: passMark,
      // Version this editor opened: the API refuses the save if someone saved a newer one meanwhile
      version: quiz.version ?? 0,
      questions: (quiz.questions || []).map(normalizeQuestion),
      timeLimitMinutes: quiz.timeLimitMinutes ? String(quiz.timeLimitMinutes) : '',
    };
    setEditingId(quiz.id);
    setFormData(next);
    openedWith.current = JSON.stringify(next);
    setFormError(null);
    setShowForm(true);
  };

  const handleDelete = async (quizId) => {
    const ok = await confirm({
      title: t('courseAssess.quizzes.deleteTitle'),
      description: t('courseAssess.quizzes.deleteDesc'),
      confirmLabel: t('courseAssess.quizzes.deleteAction'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await deleteQuiz(courseId, quizId);
      // Silent: the list refreshes in place instead of flashing the page skeleton
      await fetchAll(true);
      toast.success(t('courseAssess.quizzes.quizDeleted'));
    } catch (err) {
      toast.error(errorText(err, t('courseAssess.quizzes.deleteFailed')));
    }
  };

  const handleSubmit = async () => {
    const timeLimit = readTimeLimit(formData.timeLimitMinutes);
    if (!timeLimit.valid) { setFormError(t('quizAuthoring.quiz.timeLimitInvalid')); return; }
    if (!readPassMark(formData.passingScore).valid) { setFormError(t('quizAuthoring.common.passMarkInvalid')); return; }
    if (formData.questions.length === 0) { setFormError(t('courseAssess.quizzes.addQuestionBeforeSave')); return; }
    const emptyQs = formData.questions.filter(q => !q.text.trim());
    if (emptyQs.length > 0) {
      setFormError(t(pluralKey('courseAssess.quizzes.questionsNoText', emptyQs.length, lang), { count: emptyQs.length }));
      return;
    }
    const shortQs = formData.questions.filter(q => q.options.filter(o => o.trim()).length < 2);
    if (shortQs.length > 0) { setFormError(t('courseAssess.quizzes.questionNeedsOptions')); return; }
    if (formData.questions.some(q => !hasCorrectAnswer(q))) { setFormError(t('courseAssess.quizzes.questionNeedsCorrectAnswer')); return; }
    if (!editingId && !formData.lessonId) { setFormError(t('courseAssess.quizzes.selectLessonForQuiz')); return; }
    setSaving(true);
    setFormError(null);
    try {
      const passMark = Number(formData.passingScore);
      const sendPassMark = formData.passingScore !== '' && Number.isFinite(passMark)
        && (!editingId || String(formData.passingScore) !== String(formData.initialPassingScore));
      const payload = {
        lessonId: formData.lessonId || undefined,
        passingScore: sendPassMark ? passMark : undefined,
        questions: formData.questions.map(toApiQuestion),
        // null clears the limit; leaving the field out would keep the old one
        timeLimitMinutes: timeLimit.minutes,
        expectedVersion: formData.version,
      };
      if (editingId) {
        await updateQuiz(courseId, editingId, payload);
        toast.success(t('courseAssess.quizzes.quizSaved'));
      } else {
        await createQuiz(formData.lessonId, payload);
        toast.success(t('courseAssess.quizzes.quizCreated'));
      }
      await fetchAll(true);
      setShowForm(false);
    } catch (err) {
      // 409: the quiz moved on while this editor was open. Say so in the teacher's language and refresh
      // the list behind the editor, so that closing and reopening it starts from the saved version.
      // (The API client rejects with the HTTP status in `code`; there is no err.response.)
      const moved = err?.code === 409;
      setFormError(moved
        ? t('courseAssess.quizzes.versionConflict')
        : errorText(err, t('courseAssess.quizzes.saveQuizFailed')));
      if (moved) fetchAll(true);
    } finally {
      setSaving(false);
    }
  };

  // Back out of the editor: a quiz with unsaved changes asks first, an untouched one just closes
  const closeEditor = async () => {
    if (JSON.stringify(formData) !== openedWith.current) {
      const leave = await confirm({
        title: t('courseAssess.quizzes.discardTitle'),
        description: t('courseAssess.quizzes.discardDesc'),
        confirmLabel: t('courseAssess.quizzes.discardAction'),
        tone: 'danger',
      });
      if (!leave) return;
    }
    setShowForm(false);
  };

  const toggleModule = (id) => setCollapsed(prev => ({ ...prev, [id]: !prev[id] }));
  const hasData = quizzes.length > 0 || lessons.length > 0 || modules.length > 0;
  const canCreate = totalMissing > 0;

  if (loading) {
    return (
      <div className="cq-page">
        <LoadingState skeleton="page" rows={6} message={t('courseAssess.quizzes.loadingTests')} />
      </div>
    );
  }

  if (showForm) {
    return (
      <>
        <QuizEditor
          editingId={editingId}
          formData={formData}
          setFormData={setFormData}
          lessons={lessons}
          lessonNumbers={lessonNumbers}
          modules={modules}
          quizByLesson={quizByLesson}
          courseId={courseId}
          saving={saving}
          formError={formError}
          embedded={embedded}
          onSave={handleSubmit}
          onClose={closeEditor}
        />
        {confirmDialog}
      </>
    );
  }

  if (loadError && !hasData) {
    return (
      <div className="cq-page">
        {loadError === 'forbidden' ? (
          <PageEmptyState variant="forbidden" size="sm" title={t('courseAssess.quizzes.accessDenied')} />
        ) : (
          <PageEmptyState variant="error" size="sm" onRetry={() => fetchAll()} />
        )}
      </div>
    );
  }

  const covered = Math.max(0, lessons.length - totalMissing);
  const coverage = lessons.length ? Math.round((covered / lessons.length) * 100) : 0;
  const goAddLesson = () => navigate(addLessonPath(courseId));
  // A test belongs to a lesson: with none yet, the button leads to adding one instead of sitting disabled
  const createButton = lessons.length === 0 ? (
    <button type="button" className="ds-btn ds-btn-primary cq-create" onClick={goAddLesson} title={t('quizAuthoring.quiz.lessonsFirstHint')}>
      <Plus aria-hidden="true" />
      {t('quizAuthoring.common.addLessonAction')}
    </button>
  ) : (
    <button
      type="button"
      className="ds-btn ds-btn-primary cq-create"
      onClick={() => openCreate()}
      disabled={!canCreate}
      title={canCreate ? undefined : t('courseAssess.quizzes.createDisabledHint')}
    >
      <Plus aria-hidden="true" />
      {t('courseAssess.quizzes.createQuiz')}
    </button>
  );

  return (
    <div className="cq-page">
      {embedded ? (
        <div className="cq-intro">
          <p className="cq-lead">{t('courseAssess.quizzes.pageLead')}</p>
          {createButton}
        </div>
      ) : (
        <header className="page-head">
          <div className="page-head-main">
            <span className="page-head-icon"><ListChecks aria-hidden="true" /></span>
            <div>
              <h1 className="page-title">{t('courseAssess.quizzes.pageTitle')}</h1>
              <p className="page-sub">{t('courseAssess.quizzes.pageLead')}</p>
            </div>
          </div>
          <div className="page-actions">{createButton}</div>
        </header>
      )}

      <div className="kpi-row">
        <MetricCard countUp={false} title={t('courseAssess.quizzes.totalTests')} value={quizzes.length} icon={<ClipboardCheck />} tone="info" />
        <MetricCard countUp={false} title={t('courseAssess.quizzes.coverage')} value={`${coverage}%`} icon={<CircleCheck />} tone="success" />
        <MetricCard countUp={false} title={t('courseAssess.quizzes.withoutTest')} value={totalMissing} icon={<TriangleAlert />} tone="coral" />
        <MetricCard countUp={false} title={t('courseAssess.quizzes.modules')} value={modules.length} icon={<Layers />} tone="purple" />
      </div>

      {loadError && (
        <div className="ui-alert" role="alert">
          <TriangleAlert aria-hidden="true" />
          <p>{t('ui.errorTitle')}. {t('ui.errorDescription')}</p>
        </div>
      )}

      <div className="cq-toolbar">
        <SearchInput
          className="cq-search-wrap"
          value={search}
          onChange={setSearch}
          placeholder={t('courseAssess.quizzes.searchPlaceholder')}
          aria-label={t('courseAssess.quizzes.searchPlaceholder')}
        />
        <div className="cq-filters">
          <Dropdown
            value={moduleFilter}
            onChange={(val) => setModuleFilter(val)}
            options={[
              { value: '', label: t('courseAssess.quizzes.allModules'), separator: true },
              ...modules.map(m => ({ value: m.id, label: m.title })),
            ]}
            placeholder={t('courseAssess.quizzes.allModules')}
            ariaLabel={t('courseAssess.quizzes.modules')}
            size="sm"
          />
          {/* The label says this one orders the list; it is not another filter */}
          <div className="cq-sort">
            <label className="cq-sort-label" htmlFor="cq-sort">{t('courseAssess.quizzes.sortLabel')}</label>
            <Dropdown
              id="cq-sort"
              value={sort}
              onChange={(val) => setSort(val)}
              options={[
                { value: 'order', label: t('courseAssess.quizzes.sortLessonOrder') },
                { value: 'missing', label: t('courseAssess.quizzes.sortMissingFirst') },
                { value: 'alpha', label: t('courseAssess.quizzes.sortAlpha') },
              ]}
              ariaLabel={t('courseAssess.quizzes.sortLabel')}
              size="sm"
            />
          </div>
        </div>
      </div>

      {filteredGroups.length === 0 ? (
        <PageEmptyState
          variant={search || moduleFilter ? 'search' : 'docs'}
          size="sm"
          title={search || moduleFilter ? t('courseAssess.quizzes.noResultsSearch') : t('courseAssess.quizzes.noLessonsFound')}
          description={search || moduleFilter ? undefined : t('courseAssess.quizzes.noLessonsFoundDesc')}
          actionLabel={search || moduleFilter ? undefined : t('quizAuthoring.common.addLessonAction')}
          onAction={search || moduleFilter ? undefined : goAddLesson}
          secondaryLabel={search || moduleFilter ? t('courseAssess.quizzes.clearFiltersAction') : undefined}
          onSecondaryAction={search || moduleFilter ? () => { setSearch(''); setModuleFilter(''); } : undefined}
        />
      ) : (
        <div className="cq-groups">
          {filteredGroups.map(({ module, rows }) => {
            const withQuiz = rows.filter(r => r.quiz).length;
            const missing = rows.length - withQuiz;
            const isCollapsed = collapsed[module.id];
            const bodyId = `cq-module-${module.id}`;
            return (
              <section className="section-card cq-module" key={module.id}>
                <button
                  type="button"
                  className="cq-module-hd"
                  onClick={() => toggleModule(module.id)}
                  aria-expanded={!isCollapsed}
                  aria-controls={bodyId}
                >
                  <ChevronDown className={`cq-module-chevron ${isCollapsed ? 'is-closed' : ''}`} size={18} aria-hidden="true" />
                  <span className="cq-module-name">{module.title}</span>
                  <span className="cq-module-stats">{t(pluralKey('courseAssess.quizzes.lessonsCountLabel', rows.length, lang), { count: rows.length })} · {t(pluralKey('courseAssess.quizzes.quizzesCountLabel', withQuiz, lang), { count: withQuiz })}</span>
                  {missing > 0 && <span className="ds-badge ds-badge-warning cq-module-badge">{t('courseAssess.quizzes.missingPill', { count: missing })}</span>}
                  {missing === 0 && rows.length > 0 && <span className="ds-badge ds-badge-success ds-badge--dot cq-module-badge">{t('courseAssess.quizzes.completePill')}</span>}
                </button>

                {!isCollapsed && (
                  <div id={bodyId}>
                    <LessonTable
                      label={`${module.title}: ${t('courseAssess.quizzes.lessonsTableAria')}`}
                      rows={rows}
                      lessonNumbers={lessonNumbers}
                      courseId={courseId}
                      onCreate={openCreate}
                      onEdit={openEdit}
                      onDelete={handleDelete}
                    />
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}
      {confirmDialog}
    </div>
  );
}
